import { useEffect, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Chip,
  IconButton,
  Avatar,
  Alert,
} from '@mui/material'
import { Close, GetApp, Person } from '@mui/icons-material'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { useNavigate } from 'react-router-dom'
import { CertificateIcon, PdfIcon, UserIcon } from 'src/v3/core/icons'
import { useAppSelector } from 'src/store/hooks'
import { selectLightweightUsersV4ByService } from 'src/v3/manage/manage.slice'
import { downloadFile } from 'src/v3/utilities/filesystem'
import {
  getCertificateUrl,
  extractCertificateType,
} from 'src/v3/manage/utils/certificate-download'
import axios from 'src/utilities/http-client'
import type { CEActivityData } from '../../types'

dayjs.extend(relativeTime)

// Utility to properly format camelCase/kebab-case strings to human readable labels
function formatLabel(str: string): string {
  return (
    str
      // Handle kebab-case (ce-completion -> CE Completion)
      .replace(/-/g, ' ')
      // Handle camelCase but preserve consecutive capitals (certificateID -> Certificate ID, ceHours -> CE Hours)
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      // Split consecutive capitals (ID, CE) from other letters
      .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
      // Capitalize each word
      .replace(/\b\w/g, (l) => l.toUpperCase())
      // Fix specific cases where we want all caps
      .replace(/\bCe\b/g, 'CE')
      .replace(/\bId\b/g, 'ID')
  )
}

interface CertificateActivityModalProps {
  event: CEActivityData | null
  eventType: string | null
  open: boolean
  onClose: () => void
  linkedServiceID: string
}

export default function CertificateActivityModal({
  event,
  eventType,
  open,
  onClose,
  linkedServiceID,
}: CertificateActivityModalProps) {
  let navigate = useNavigate()

  // Download state
  let [downloading, setDownloading] = useState(false)
  let [errorMessage, setErrorMessage] = useState('')
  let [profileImage, setProfileImage] = useState<string>('')

  // Get lightweight users to find basic user info
  let lightweightUsers = useAppSelector(
    selectLightweightUsersV4ByService(linkedServiceID),
  )
  let lightweightUser = lightweightUsers.find((u) => u.id === event?.userID)

  // Fetch profile image
  useEffect(() => {
    if (open && event?.userID) {
      axios
        .get(`/api/v2/manage/profile-image?userID=${event.userID}`)
        .then(({ data }) => data)
        .then(setProfileImage)
        .catch(() => setProfileImage(''))
    }
  }, [open, event?.userID])

  // Handle certificate download
  let handleDownload = async () => {
    if (!event?.certificateID) return

    setDownloading(true)
    setErrorMessage('')

    try {
      let certificateType = extractCertificateType(eventType || undefined)
      let response = await getCertificateUrl(
        event.certificateID,
        certificateType,
      )
      let fileResponse = await axios.get(response, { responseType: 'blob' })
      let filename = event.certificateFileName || `${event.certificateID}.pdf`

      await downloadFile(fileResponse.data, filename)
    } catch (err: any) {
      setErrorMessage(
        `PDF Download Failed: ${err.response?.data || err.message || 'Unknown error'}`,
      )
    } finally {
      setDownloading(false)
    }
  }

  if (!event) {
    return null
  }

  let hasUserInfo = lightweightUser || event

  // Format event data for display
  let displayData = [
    { key: 'Certificate ID', value: event.certificateID, type: 'text' },
    { key: 'Activity Type', value: event.activityType, type: 'text' },
    { key: 'Category', value: event.category, type: 'text' },
    { key: 'Course Name', value: event.courseName, type: 'text' },
    { key: 'Course Code', value: event.courseCode, type: 'text' },
    { key: 'CE Hours', value: event.ceHours, type: 'number' },
    { key: 'Completion Date', value: event.completionDate, type: 'datetime' },
    {
      key: 'License Types',
      value: event.licenseTypes.join(', '),
      type: 'text',
    },
  ].filter((item) => item.value)

  let formatDisplayValue = (value: any, type: string): string => {
    if (!value) return 'N/A'

    switch (type) {
      case 'datetime':
        return dayjs(value).format('MMM D, YYYY [at] h:mm A')
      case 'date':
        return dayjs(value).format('MMM D, YYYY')
      case 'number':
        return String(value)
      default:
        return String(value)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            width: '80vw',
            height: '80vh',
            maxWidth: 'none',
            maxHeight: 'none',
            borderRadius: 2,
            bgcolor: 'background.default',
          },
        },
      }}
    >
      <DialogTitle>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6">Certificate Activity</Typography>
            <Chip
              label={formatLabel(event.activityType)}
              sx={{
                bgcolor: 'entityColors.ceActivity',
                color: 'entityColors.ceActivity',
                fontWeight: 500,
                '&.MuiChip-root': {
                  backgroundColor: (theme) =>
                    `${theme.palette.entityColors.ceActivity}20`,
                  color: (theme) => theme.palette.entityColors.ceActivity,
                },
              }}
            />
          </Box>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 2 }}>
        {/* Event Metadata */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            {dayjs(event.completionDate).format('MMMM D, YYYY [at] h:mm A')} •{' '}
            {dayjs(event.completionDate).fromNow()}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {event.courseName} {event.courseCode && `(${event.courseCode})`}
          </Typography>
        </Box>

        {/* Main Content Grid */}
        <Box className="grid grid-cols-1 gap-4 mb-4 lg:grid-cols-2">
          {/* User Information Section */}
          <Paper sx={{ p: 2, bgcolor: 'background.paperDark' }}>
            <Typography
              variant="h6"
              sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <UserIcon />
              User Information
            </Typography>

            {hasUserInfo ? (
              <>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}
                >
                  {profileImage ? (
                    <Avatar
                      sx={{
                        width: 64,
                        height: 64,
                      }}
                      src={profileImage}
                    />
                  ) : (
                    <Avatar
                      sx={{
                        width: 64,
                        height: 64,
                        bgcolor: 'primary.main',
                        fontSize: '1.5rem',
                        fontWeight: 600,
                      }}
                    >
                      {(
                        lightweightUser?.firstName?.[0] ||
                        event.userName?.[0] ||
                        '?'
                      ).toUpperCase()}
                    </Avatar>
                  )}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {lightweightUser?.firstName && lightweightUser?.lastName
                        ? `${lightweightUser.firstName} ${lightweightUser.lastName}`
                        : event.userName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {lightweightUser?.primaryEmail || event.userEmail}
                    </Typography>
                  </Box>
                </Box>

                {lightweightUser?.licenseTypes &&
                  lightweightUser.licenseTypes.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mb: 1 }}
                      >
                        License Types
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {lightweightUser.licenseTypes.map((licenseType) => (
                          <Chip
                            key={licenseType}
                            label={licenseType}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    </Box>
                  )}

                <Button
                  variant="outlined"
                  startIcon={<Person />}
                  onClick={() => {
                    onClose()
                    navigate(
                      `/manage/roster/${linkedServiceID}/user/${lightweightUser?.id}`,
                    )
                  }}
                  size="small"
                >
                  View User Profile
                </Button>
              </>
            ) : (
              <Box sx={{ py: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  User information not available
                </Typography>
              </Box>
            )}
          </Paper>

          {/* Certificate Section */}
          <Paper
            sx={{
              p: 2,
              bgcolor: 'background.paperDark',
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
            }}
          >
            <Typography
              variant="h6"
              sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <CertificateIcon />
              Certificate Details
            </Typography>

            <Box className="grid grid-cols-2 gap-4 mb-4">
              <Box>
                <Typography variant="body2" color="text.secondary">
                  CE Hours Earned
                </Typography>
                <Typography
                  variant="h4"
                  color="success.main"
                  sx={{ fontWeight: 700 }}
                >
                  {event.ceHours}
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  Category
                </Typography>
                <Chip
                  label={event.category}
                  color="primary"
                  variant="outlined"
                />
              </Box>
            </Box>

            {/* Spacer to push button to bottom */}
            <Box sx={{ flexGrow: 1 }} />

            {errorMessage && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errorMessage}
              </Alert>
            )}

            <Button
              variant="contained"
              startIcon={!downloading ? <PdfIcon /> : undefined}
              onClick={handleDownload}
              disabled={!event.certificateID}
              loading={downloading}
              fullWidth
            >
              {downloading
                ? 'Downloading Certificate...'
                : 'Download Certificate'}
            </Button>
          </Paper>
        </Box>

        {/* Activity Details Section */}
        <Paper sx={{ p: 2, bgcolor: 'background.paperDark' }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Activity Details
          </Typography>

          <Box className="grid grid-cols-1 gap-1 sm:grid-cols-2">
            {displayData.map(({ key, value, type }) => (
              <Box key={key} sx={{ p: 1 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block' }}
                >
                  {formatLabel(key)}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {formatDisplayValue(value, type)}
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onClose}>Close</Button>
        {event.certificateID && (
          <Button
            startIcon={!downloading ? <GetApp /> : undefined}
            variant="outlined"
            onClick={handleDownload}
            loading={downloading}
          >
            {downloading ? 'Downloading...' : 'Download'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
